import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { FileText, Download, Building2, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function AdminReports() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchAvailableMonths(selectedCompany);
    } else {
      setAvailableMonths([]);
      setSelectedMonth('');
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMonths = async (companyId) => {
    try {
      const response = await axios.get(`${API}/reports/available-months/${companyId}`);
      setAvailableMonths(response.data);
      if (response.data.length > 0) {
        setSelectedMonth(response.data[0]); // Select most recent
      }
    } catch (error) {
      toast.error('Error al cargar meses disponibles');
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
  };

  const generateReport = async () => {
    if (!selectedCompany || !selectedMonth) {
      toast.error('Selecciona empresa y mes');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.get(
        `${API}/reports/company/${selectedCompany}?month=${selectedMonth}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const company = companies.find(c => c.id === selectedCompany);
      const filename = `Informe_${company?.name.replace(/\s+/g, '_')}_${selectedMonth}.pdf`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Informe generado correctamente');
    } catch (error) {
      toast.error('Error al generar informe');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Layout title="Informes">
      <div className="space-y-6 animate-fade-in" data-testid="admin-reports">
        <p className="text-slate-400">Genera informes mensuales de mantenimiento para cada empresa</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Generator Card */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="font-rajdhani text-lg text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-sky-400" />
                Generar Informe PDF
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-sm text-slate-300 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Empresa
                </label>
                <Select
                  value={selectedCompany || undefined}
                  onValueChange={setSelectedCompany}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="report-company-select">
                    <SelectValue placeholder="Selecciona una empresa" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Mes
                </label>
                <Select
                  value={selectedMonth || undefined}
                  onValueChange={setSelectedMonth}
                  disabled={!selectedCompany || availableMonths.length === 0}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="report-month-select">
                    <SelectValue placeholder={selectedCompany ? "Selecciona un mes" : "Primero selecciona empresa"} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {availableMonths.map((m) => (
                      <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={generateReport}
                disabled={!selectedCompany || !selectedMonth || generating}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white gap-2"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Descargar Informe PDF
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="font-rajdhani text-lg text-white">
                Contenido del Informe
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <div>
                    <p className="font-medium text-white">Resumen General</p>
                    <p className="text-sm text-slate-400">Estadísticas del mes: trabajos, incidencias, estado de máquinas</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <div>
                    <p className="font-medium text-white">Trabajos Realizados</p>
                    <p className="text-sm text-slate-400">Detalle de cada trabajo: fecha, tipo, máquina, descripción, técnico</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <div>
                    <p className="font-medium text-white">Incidencias</p>
                    <p className="text-sm text-slate-400">Incidencias reportadas con prioridad y estado actual</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                  <div>
                    <p className="font-medium text-white">Estado de Máquinas</p>
                    <p className="text-sm text-slate-400">Listado completo con estado actual y repuestos pendientes</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
